const EVENT_TYPE_LABELS = {
  transfer: 'Transferred',
  cancel: 'Cancelled',
  refund: 'Refunded',
  reclassification: 'Reclassified',
  split: 'Split',
  admit: 'Admitted',
  modified: 'Details updated',
  manual_create: 'Manually created',
  purchase: 'Purchased',
}

const EVENT_TYPE_ICON_CLASS = {
  transfer: 'history-icon--transfer',
  cancel: 'history-icon--cancel',
  refund: 'history-icon--refund',
  reclassification: 'history-icon--reclass',
  split: 'history-icon--split',
  admit: 'history-icon--admit',
  modified: 'history-icon--edit',
  manual_create: 'history-icon--create',
  purchase: 'history-icon--purchase',
}

const OUTCOME_LABELS = {
  success: 'Success',
  pending: 'Pending',
  failed: 'Failed',
}

const OUTCOME_CHIP_CLASS = {
  success: 'Chip--success',
  pending: 'Chip--warning',
  failed: 'TicketHistory-chip--failed',
}

const ADMIT_STATUS_LABELS = {
  0: 'Not admitted',
  1: 'Admitted',
}

const PII_PAYLOAD_KEYS = new Set([
  'email',
  'phone',
  'forename',
  'surname',
  'name',
  'purchaser_email',
  'purchaser_phone',
  'purchaser_name',
])

export function getEventTypeLabel(eventType) {
  if (!eventType) {
    return 'Unknown'
  }
  return EVENT_TYPE_LABELS[eventType] || eventType
}

export function getEventTypeIconClass(eventType) {
  return EVENT_TYPE_ICON_CLASS[eventType] || 'history-icon--default'
}

export function getOutcomeLabel(outcome) {
  return OUTCOME_LABELS[outcome] || outcome || 'Unknown'
}

export function getOutcomeChipClass(outcome) {
  return OUTCOME_CHIP_CLASS[outcome] || 'Chip'
}

export function formatMinorUnits(minorUnits, currency = 'GBP') {
  const amount = Number(minorUnits)
  if (Number.isNaN(amount)) {
    return ''
  }
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
  }).format(amount / 100)
}

function formatEventRef(eventId) {
  if (eventId == null || eventId === '') {
    return '?'
  }
  return `#${eventId}`
}

function formatAdmitStatus(status) {
  if (status == null || status === '') {
    return String(status)
  }
  const key = Number(status)
  return ADMIT_STATUS_LABELS[key] != null ? ADMIT_STATUS_LABELS[key] : String(status)
}

function formatQuantityChange(before, after) {
  if (before == null && after == null) {
    return ''
  }
  return `Quantity ${before ?? '?'} → ${after ?? '?'}`
}

export function formatPayloadSummary(eventType, payload = {}) {
  switch (eventType) {
    case 'transfer': {
      const line = `Event ${formatEventRef(payload.event_id_before)} → ${formatEventRef(payload.event_id_after)}`
      const settlement = payload.settlement
      if (settlement && settlement.method != null) {
        const delta = formatMinorUnits(
          settlement.delta_minor_units,
          settlement.currency || 'GBP'
        )
        return `${line} · ${settlement.method}${delta ? ` ${delta}` : ''}`
      }
      return line
    }
    case 'cancel':
      return formatQuantityChange(payload.quantity_before, payload.quantity_after)
    case 'refund': {
      const base = formatQuantityChange(payload.quantity_before, payload.quantity_after)
      const reason = payload.refund && payload.refund.reason
      return reason ? `${base} · ${reason}` : base
    }
    case 'split': {
      const codes = payload.split_ticket_codes || []
      if (!codes.length) {
        return 'Split'
      }
      return `Split into ${codes.join(', ')}`
    }
    case 'admit':
      return `Status ${formatAdmitStatus(payload.status_before)} → ${formatAdmitStatus(payload.status_after)}`
    case 'modified': {
      const field = payload.field || payload.updated_field
      return field ? `Field updated: ${field}` : 'Details updated'
    }
    case 'reclassification': {
      const parts = []
      if (payload.related_ticket_code) {
        parts.push(`Related ticket: ${payload.related_ticket_code}`)
      }
      if (payload.quantity_before != null || payload.quantity_after != null) {
        parts.push(formatQuantityChange(payload.quantity_before, payload.quantity_after))
      }
      return parts.length ? parts.join(' · ') : 'Reclassified'
    }
    case 'manual_create':
      return payload.ticket_code ? `Ticket ${payload.ticket_code} created` : 'Manually created'
    default:
      return ''
  }
}

export function formatActivityTimestamp(isoString) {
  if (!isoString) {
    return ''
  }
  const date = new Date(isoString)
  if (Number.isNaN(date.getTime())) {
    return isoString
  }

  const formatted = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date)

  const tzPart = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    timeZoneName: 'short',
  })
    .formatToParts(date)
    .find((part) => part.type === 'timeZoneName')

  const tz = tzPart ? tzPart.value : 'GMT'
  return `${formatted} ${tz}`
}

export function truncateOperationId(operationId, visibleLength = 8) {
  if (!operationId) {
    return ''
  }
  const id = String(operationId)
  if (id.length <= visibleLength) {
    return id
  }
  return `${id.slice(0, visibleLength)}…`
}

export function getRelatedTicketCodes(entry = {}) {
  const payload = entry.payload || {}
  const codes = []

  if (payload.related_ticket_code) {
    codes.push(String(payload.related_ticket_code))
  }
  if (Array.isArray(payload.split_ticket_codes)) {
    payload.split_ticket_codes.forEach((code) => {
      const value = String(code)
      if (!codes.includes(value)) {
        codes.push(value)
      }
    })
  }

  return codes
}

export function sanitizePayloadForDisplay(payload) {
  if (!payload || typeof payload !== 'object') {
    return payload
  }

  const clone = JSON.parse(JSON.stringify(payload))
  const stripPii = (obj) => {
    if (!obj || typeof obj !== 'object') {
      return
    }
    Object.keys(obj).forEach((key) => {
      if (PII_PAYLOAD_KEYS.has(key.toLowerCase())) {
        delete obj[key]
        return
      }
      if (obj[key] && typeof obj[key] === 'object') {
        stripPii(obj[key])
      }
    })
  }
  stripPii(clone)
  return clone
}
